import React from 'react';
import { eventChannel } from 'redux-saga';
import { defineMessages, FormattedMessage } from 'react-intl';
import {
  put, select, take, call, 
} from 'redux-saga/effects';
import nsApi from '@netsapiens/netsapiens-js/dist/api';
import socketIO from 'socket.io-client';
import _ from 'lodash';

import * as actions from '../state';
import bugsnagClient from '../bugsnag';
// eslint-disable-next-line import/no-cycle
import store from '../store';

const messages = defineMessages({
  SOCKET_LOST_CONNECTION_TOAST: {
    id: 'SOCKET_LOST_CONNECTION_TOAST',
    defaultMessage: 'Lost connection. Trying to reconnect...',
  },
  SOCKET_NO_CONNECTION_TOAST: {
    id: 'SOCKET_NO_CONNECTION_TOAST',
    defaultMessage: 'No connection. Trying to reconnect...',
  },
});

const _sendError = (message, opt) => {
  const { onLine } = navigator; // get network status at the time of the error

  // the loop handles offline cases
  // the error will send after the browser is back online
  const interval = setInterval(() => {
    if (navigator.onLine) {
      bugsnagClient.notify(new Error(message), { metaData: { ...opt, onLine } });
      clearInterval(interval);
    }
  }, 10000);
};

// eslint-disable-next-line import/prefer-default-export
export function* nsSocketConnect() {
  const nsSocketURLs = yield select((state) => actions.selectConfig(state, 'nsSocketURLs'));

  try {
    const socket = socketIO.connect(nsSocketURLs[0], { secure: true });

    // create channel for callbacks
    const channel = eventChannel((emitter) => {
      socket.on('error', () => {
        emitter('error');
      });

      socket.on('reconnect', () => {
        emitter('reconnect');
      });

      socket.on('reconnect_attempt', () => {
        emitter('reconnect_attempt');
      });

      socket.on('reconnecting', () => {
        emitter('reconnecting');
      });

      socket.on('reconnect_error', () => {
        emitter('reconnect_error');
      });

      socket.on('reconnect_failed', () => {
        emitter('reconnect_failed');
      });

      socket.on('disconnect', () => {
        emitter('disconnect');
      });

      socket.on('connect', () => {
        emitter('connect');
      });

      // unsubscribe method
      return () => {
        socket.disconnect();
      };
    });

    while (true) {
      const res = yield take(channel);

      let s;
      switch (res) {
        case 'connect': {
          s = yield select((state) => actions.selectSocket(state));
          if (!s) {
            yield put(actions.setSocket(socket));
          }
          yield put(actions.setConnected(true));
          const subscriptions = yield select((state) => actions.selectSubscriptions(state));
          const boardCharts = _.reduce(actions.readStateCharts(store.getState()), (obj, param) => {
            obj[param.id] = param; // eslint-disable-line
            return obj;
          }, {});

          for (let i = 0; i < subscriptions.length; i += 1) {
            const sub = subscriptions[i];
            // filter out the subscriptions that are no longer needed
            if (boardCharts[sub.chartId]) {
              // eslint-disable-next-line no-undef
              const clonedSub = _.cloneDeep(sub);
              if (boardCharts[sub.chartId].data_token) {
                clonedSub.bearer = boardCharts[sub.chartId].data_token.access_token;
              } else {
                clonedSub.bearer = yield call(nsApi.getToken);
              }              
              clonedSub.application = 'analytics';
              socket.emit('subscribe', clonedSub);
            }
          }
          break;
        }
        case 'disconnect':
        case 'error': {
          console.error('Socket connection error');
          yield put(actions.setConnected(false));
          const isNetworkConnected = yield select((state) => actions.selectNetworkConnected(state));

          // avoid repeats of errors in the ui, network connection errors will trigger a message and bugsnag error
          if (isNetworkConnected) {
            yield put(actions.showToaster({
              show: true,
              message: <FormattedMessage {...messages.SOCKET_LOST_CONNECTION_TOAST} />,
              type: 'error',
            }));

            _sendError(`Socket ${res}`, { connectionURL: nsSocketURLs[0] });
          }
          break;
        }
        case 'reconnect_attempt': {
          s = yield select((state) => actions.selectSocket(state));
          const isConnected = yield select((state) => actions.selectIsConnected(state));

          // only show the error for initial load connection errors
          if (s === null && isConnected === null) {
            yield put(actions.setConnected(false));
            yield put(actions.showToaster({
              show: true,
              message: <FormattedMessage {...messages.SOCKET_NO_CONNECTION_TOAST} />,
              type: 'error',
            }));
            _sendError('Initial socket connection error', { connectionURL: nsSocketURLs[0] });
          }
          break;
        }
        default:
      }
    }
  } catch (e) {
    yield put(actions.setSocket(false));
    yield put(actions.setConnected(false));
    yield put(actions.showToaster({
      show: true,
      message: <FormattedMessage {...messages.SOCKET_NO_CONNECTION_TOAST} />,
      type: 'error',
    }));
    _sendError('Initial socket connection error', { connectionURL: nsSocketURLs[0] });
  }
}
